/* GrowthOS — Price Sheet record (view) page.
   Rebuilt to the shared `.gos-leadrec` / `gos-lr-*` detail shell (mirrors Product /
   Sales / Quote detail pages): back row, header card (tag glyph + name + Active badge
   + pricing/currency meta, owner pill, Edit + dots), chips, a horizontal tab bar, a
   read-only Overview (info cards) and the real Products (CommonProductList) / Accounts
   (ChildAccount) / Activity(Timeline) / Notes child tabs. Replaces the legacy left-nav
   scroll-spy that reused PriceForm in "view mode" (PriceForm is now a plain editable
   form). Same fetches as the legacy page (fetchDetail/Notes/AccountsAndAppendToPriceSheet/
   ProductsAndAppendToModule, module 'PriceSheet'). */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAccountsAndAppendToPriceSheet,
  fetchDetailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchProductsAndAppendToModule,
} from '../../redux/actions';
import { getPriceSheetById } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Timeline from '../sharedChild/timeline';
import ChildAccount from '../sharedChild/childAccount';
import CommonProductList from '../sharedChild/commonList/product/commonProductList';

const ICONS = {
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  tag: (<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" /><circle cx="7" cy="7" r="1.4" /></>),
  box: <path d="M21 8l-9-5-9 5 9 5 9-5ZM3 8v8l9 5 9-5V8M12 13v8" />,
  calendar: <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />,
  percent: (<><path d="M19 5 5 19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>),
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" />,
  chevL: <path d="M15 18l-6-6 6-6" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';
const money = (n) => `${CURRENCY} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const val = (v) => (v === 0 || v ? v : null);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const activeBadge = (on) => (
  <span className={'gos-badge gos-b-' + (on ? 'green' : 'gray')}>
    <span className="gos-pip" />
    {on ? 'Active' : 'Inactive'}
  </span>
);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);

const TextCard = ({ title, icon, text }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className={'gos-lr-textbody' + (text ? '' : ' muted')}>{text || 'No content provided.'}</div>
  </div>
);

export const ViewPricelist = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'priceSheetList');
  const [loading, setLoading] = useState(storeLoading);

  const searchParams = new URLSearchParams(location.search);
  const priceSheetID = searchParams.get('id');

  const [priceSheetLocalData, setPriceSheetLocalData] = useState({ ...(location.state || {}), type: 'view', from: 'view' });
  const priceSheetStoreData = useSelector((state) => getPriceSheetById(state, location.state?.id || priceSheetID));

  useEffect(() => {
    setPriceSheetLocalData((prev) => ({ ...prev, ...priceSheetStoreData }));
  }, [priceSheetStoreData]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const priceSheetId = location.state?.id || priceSheetID;
    if (priceSheetId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'PriceSheet', id: priceSheetId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'PriceSheet', id: priceSheetId }));
      dispatch(fetchAccountsAndAppendToPriceSheet({ moduleName: 'PriceSheet', id: priceSheetId }));
      dispatch(fetchProductsAndAppendToModule({ moduleName: 'PriceSheet', id: priceSheetId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, priceSheetLocalData?.id]);

  const cd = priceSheetLocalData || {};
  const owner = cd.priceSheetOwner;
  const active = !!cd.productActive;

  const handleEditPriceSheet = () => {
    setMenuOpen(false);
    navigate('/products/priceSheet/editPriceSheet', { state: { ...cd, type: 'view', from: 'view' } });
  };

  const chips = [
    cd.priceModel ? { icon: 'tag', label: cd.priceModel } : null,
    { icon: 'box', label: active ? 'Active' : 'Inactive', tone: active ? 'green' : 'gray' },
    owner && { icon: 'user', label: 'Owner ' + owner },
    cd.createdDateTime && { icon: 'clock', label: 'Created ' + fmtDT(cd.createdDateTime) },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'products', label: 'Products', icon: 'box' },
    { id: 'accounts', label: 'Accounts', icon: 'building' },
    { id: 'activity', label: 'Activity', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
  ];

  const overview = (
    <>
      <div className="gos-lr-grid2">
        <InfoCard
          title="Price Sheet Information"
          icon="tag"
          rows={[
            ['Price Sheet Owner', val(owner)],
            ['Pricing Model', val(cd.priceModel)],
            ['Currency', val(cd.currencyPrice)],
            ['Active', activeBadge(active)],
          ]}
        />
        <InfoCard
          title="Validity"
          icon="calendar"
          rows={[
            ['Start Date', val(fmtDate(cd.startDate))],
            ['End Date', val(fmtDate(cd.endDate))],
            ['Created Date', val(fmtDT(cd.createdDateTime))],
          ]}
        />
        <InfoCard
          title="Discount"
          icon="percent"
          rows={[
            ['Discount Percent', val(cd.discountPercent) != null ? cd.discountPercent + '%' : null],
            ['Discount Amount', cd.discountAmount ? money(cd.discountAmount) : null],
          ]}
        />
      </div>
      <div className="gos-lr-grid2" style={{ marginTop: 16 }}>
        <TextCard title="Terms and Conditions" icon="template" text={cd.terms} />
        <TextCard title="Description" icon="note" text={cd.description} />
      </div>
    </>
  );

  let tabBody = overview;
  if (activeTab === 'products') tabBody = <CommonProductList localData={cd} module={'PriceSheet'} />;
  else if (activeTab === 'accounts') tabBody = <ChildAccount localData={cd} module={'PriceSheet'} />;
  else if (activeTab === 'activity') tabBody = <Timeline localData={cd} module={'PriceSheet'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={cd} module={'PriceSheet'} />;

  return (
    <>
      {loading && <Loader />}
      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/products/priceSheet')}>
            <Ic name="chevL" size={15} /> Price Sheets
          </button>
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: 'var(--gos-accent-soft)', color: 'var(--gos-accent)' }}>
                <Ic name="tag" size={22} />
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {cd.name || 'Price Sheet'}
                  {activeBadge(active)}
                </div>
                <div className="gos-lr-subline">
                  <Ic name="tag" size={14} />
                  {cd.priceModel || '—'}
                  {cd.currencyPrice ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      {cd.currencyPrice}
                    </>
                  ) : null}
                  {fmtDate(cd.startDate) ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="calendar" size={14} />
                      {fmtDate(cd.startDate)}
                      {fmtDate(cd.endDate) ? ' → ' + fmtDate(cd.endDate) : ''}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              {owner ? (
                <span className="gos-qr-owner">
                  <span className="gos-lr-ava" style={{ width: 24, height: 24, fontSize: 10.5, background: gosAvaColor(owner) }}>
                    {gosInitials(owner)}
                  </span>
                  {owner}
                </span>
              ) : null}
              <button type="button" className="gos-lr-contact-btn" onClick={handleEditPriceSheet} title="Edit price sheet">
                <Ic name="edit" size={16} /> Edit
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={handleEditPriceSheet}>
                      <Ic name="edit" size={15} /> Edit Price Sheet
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className={'gos-lr-chip' + (c.tone ? ' gos-lr-chip-' + c.tone : '')} key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {tabBody}
      </div>
    </>
  );
};

export default ViewPricelist;
